import { Row, Col, Input, InputGroup, InputGroupText } from "reactstrap";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import Dashboard from "../../../components/dashboard";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import datePickerS from "react-datepicker/dist/react-datepicker.css";
import get from "lodash.get";
import isEqual from "lodash.isequal";
import { firebaseConnect } from "react-redux-firebase";
import {
  withModals,
  ModalProps,
  serviceCaller,
  ServiceCallerProps,
  withUserCharities,
  UserCharitiesProps,
  withSuperadminLoginRedirect,
} from "../../../composition";
import ICharity from "../../../../types/services/ICharity";
import * as commonActions from "../../../../utils/actions/common";
import { TableIds } from "../../../../utils/tablePrefs";

interface ICharityUrls {
  charityId: string;
  name: string;
  wlwUrl: string;
  giveUrl: string;
  disableGive: boolean;
}
interface Props extends ServiceCallerProps, ModalProps, UserCharitiesProps {
  refreshDashboard: () => void;
  hideSelection: () => void;
  charitiesUrls: Array<ICharityUrls>;
}

const columnNames = [
  { key: "Charity Name", isDisabled: false },
  { key: "WLW URL", isDisabled: false },
  { key: "Donation URL", isDisabled: false },
];

const columnData = ["name", "wlwUrl", "giveUrl"];

function useDeepEffect(fn, deps) {
  const isFirst = useRef(true);
  const prevDeps = useRef(deps);

  useEffect(() => {
    const isFirstEffect = isFirst.current;
    const isSame = prevDeps.current.every((obj, index) =>
      isEqual(obj, deps[index])
    );

    isFirst.current = false;
    prevDeps.current = deps;

    if (isFirstEffect || !isSame) {
      return fn();
    }
  }, deps);
}

function SuperadminUrlManagerPage(props: Props) {
  const [searchText, setSearchText] = useState("");

  useEffect(props.hideSelection, []);
  useDeepEffect(() => props.refreshDashboard(), [
    searchText,
    props.charitiesUrls,
  ]);

  const getResponse = (currentPage, pageSize) => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    const result = props.charitiesUrls.filter(
      (charityUrl: ICharityUrls) =>
        charityUrl.name.includes(searchText) ||
        charityUrl.wlwUrl.includes(searchText) ||
        charityUrl.charityId.includes(searchText)
    );
    return Promise.resolve({
      result: result.slice(start, end),
      totalAmount: result.length,
      time: new Date().getTime(),
      pageAmount: Math.ceil(result.length / pageSize),
    });
  };

  const showEditGiveUrlModal = (charityUrls: ICharityUrls) => {
    if (editGiveUrlDisabled(charityUrls)) return;
    const { charityId, name, giveUrl } = charityUrls;
    props.toggleEditGiveUrlModal(charityId, name, giveUrl);
  };

  const editGiveUrlDisabled = (charityUrls: ICharityUrls) => {
    return charityUrls.disableGive;
  };

  const extraColumns = [
    {
      id: "edit",
      hasTooltip: true,
      tooltipTextFunction: charityUrls =>
        editGiveUrlDisabled(charityUrls.original)
          ? "To add a donation url, please contact an admin to turn on Donations for this charity."
          : "Edit donation url",
      type: "functionalIcon",
      width: 15,
      faIconFunction: () => "fa-edit",
      className: "customColUsersIcon",
      deactivatedClassName: "customColUsersIconDeactivated",
      showFunctions: showEditGiveUrlModal,
      isDeactivated: editGiveUrlDisabled,
    },
  ];

  return (
    <main className={s.main}>
      <div className={s.container}>
        <Row className={s.marginBot}>
          <Col xs={12} md={3} className={s.colMargin + " " + s.noPadding}>
            <InputGroup>
              <InputGroupText className={s.inputGroup}>
                <i className={"fa fa-search " + s.searchIcon}/>
              </InputGroupText>
              <Input
                id="searchText"
                placeholder={"Search"}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className={s.text}
              />
              <InputGroupText
                className={s.inputGroupRight}
              >
                <i
                  className={"fa fa-times " + s.closeIcon}
                  onClick={() => setSearchText("")}
                >
                  &nbsp;
                </i>
              </InputGroupText>
            </InputGroup>
          </Col>
        </Row>
        <Row>
          {props.serviceAccessToken && props.config && (
            <Dashboard
              tableId={TableIds.urlManager}
              columnNames={columnNames}
              columnData={columnData}
              extraColumns={extraColumns}
              selectionUpdate={() => {
                return;
              }}
              checkCol={false}
              noDataText={"No Charities found"}
              sortBy={"Charity Name"} // :thinking:
              selectAll={false}
              sortAsc={true}
              footerMessage={"?"}
              currentCharity={props.currentCharity}
              getResponse={getResponse}
              searchText={searchText}
              hideSelection={true}
              showPagination={true}
              wipeData={false}
              deactivatedClassName={"deactivatedLabel"}
              showUsersFunction={null}
              preventScrollOnFetch={true}
            />
          )}
        </Row>
      </div>
    </main>
  );
}

const mapStateToProps = state => {
  const { firebase, user } = state;
  const charitiesUrls = user.charities.map((charity: ICharity) => {
    const infoLoaded = get(firebase.requested, `${charity.id}/info`, false);
    let name = infoLoaded
      ? get(firebase, `data.${charity.id}.info.name`, "No name")
      : "Loading...";

    const configLoaded =
      get(firebase.requested, `${charity.id}/config/isPrime`, false) &&
      get(firebase.requested, `${charity.id}/config/whitelabelUrl`, false) &&
      get(firebase.requested, `${charity.id}/config/disableGive`, false) &&
      get(firebase.requested, `${charity.id}/config/customDonationUrl`, false);

    const config = `data.${charity.id}.config`;

    let wlwUrl = "Loading...",
      giveUrl = "Loading...";
    const disableGive = get(firebase, `${config}.disableGive`, null);

    if (configLoaded) {
      const isPrime = get(firebase, `${config}.isPrime`, false);
      if (isPrime) name = name + " - Prime";

      wlwUrl = get(firebase, `${config}.whitelabelUrl`, null);
      giveUrl = disableGive
        ? "Giving page is disabled for this Charity"
        : get(firebase, `${config}.customDonationUrl`);
      if (!giveUrl) giveUrl = `${wlwUrl}/donate`;
    }

    return { charityId: charity.id, name, wlwUrl, giveUrl, disableGive };
  });
  return {
    charitiesUrls,
  };
};

const mapDispatchToProps = dispatch => ({
  refreshDashboard: () => {
    dispatch(commonActions.refreshDashboard());
  },
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

export default serviceCaller(
  withSuperadminLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(
      withUserCharities(
        withModals(
          firebaseConnect((_, store) => {
            return store
              .getState()
              .user.charities.map((charity: ICharity) => [
                `${charity.id}/config/isPrime`,
                `${charity.id}/config/whitelabelUrl`,
                `${charity.id}/config/customDonationUrl`,
                `${charity.id}/config/disableGive`,
                `${charity.id}/info`,
              ])
              .flat();
          })(withStyles(s, datePickerS)(SuperadminUrlManagerPage))
        )
      )
    )
  )
);
